import React from "react";

import { Modal, Typography } from "@packages/ds";
import momentjs from "moment";
import { useIntl } from "react-intl";

import { Link, ButtonWrapper, Button } from "ds/Button";

import * as S from "./BackupModal.style";

import type { EnvironmentBackup } from "@packages/client";

export interface BackupDeleteModalProps {
  backup: EnvironmentBackup;
  show: boolean;
  setShow: (value: boolean) => void;
  handleDelete: () => void;
  automated: boolean;
}

const BackupDeleteModal = ({
  backup,
  show,
  setShow,
  handleDelete,
  automated
}: BackupDeleteModalProps) => {
  const intl = useIntl();

  momentjs.updateLocale("en", {
    relativeTime: {
      past: "%s ago"
    }
  });

  const backupType = automated ? "automated" : "manual";
  const learnMore = intl.formatMessage({
    id: `backups.delete.modal.learn`,
    defaultMessage: `Learn more`
  });

  return (
    <Modal
      isOpen={show}
      size="medium"
      onOpenChange={() => setShow(!show)}
      handleClose={() => setShow(false)}
    >
      <div>
        <Typography.Title tag="h3">
          {intl.formatMessage(
            {
              id: `backups.delete.modal.title.${backupType}`,
              defaultMessage: `Delete ${backupType} backup?`
            },
            {
              id: backup.id,
              br: <br />,
              strong: text => <S.Strong>{text}</S.Strong>
            }
          )}
        </Typography.Title>
        {intl.formatMessage<React.ReactNode>(
          {
            id: `backups.delete.modal.description.${backupType}`,
            defaultMessage: `Are you sure you want to delete the ${backupType} backup ${backup?.id}`
          },
          {
            id: backup?.id,
            strong: text => <S.Strong>{text}</S.Strong>,
            date: momentjs(backup?.expires_at).fromNow()
          }
        )}
        <S.BodyText weight="semiBold" size="small">
          {intl.formatMessage<React.ReactNode>(
            {
              id: `backups.delete.modal.note.${backupType}`,
              defaultMessage: `Note: This action can not be undone ${
                automated
                  ? "and could affect your backup retention schedule."
                  : ""
              }.`
            },
            {
              a: text => (
                <Link
                  href={intl.formatMessage({
                    id: "links.documentation.backups_schedule"
                  })}
                  analyticId="links.documentation.backups_schedule"
                >
                  {text}
                </Link>
              ),
              strong: text => <S.Strong>{text}</S.Strong>,
              learnMore
            }
          )}
        </S.BodyText>
      </div>
      <ButtonWrapper spacing="modal" justifyContent="end">
        <Button
          variant="secondary"
          onClick={() => setShow(false)}
          analyticId="cancel"
        >
          {intl.formatMessage({ id: "cancel" })}
        </Button>
        <Button
          onClick={() => {
            handleDelete();
            setShow(false);
          }}
          analyticId="backup.delete_button"
        >
          {intl.formatMessage({ id: "backup.delete_button" })}
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};

export default BackupDeleteModal;
